// src/context/UserContext.jsx

import React, { createContext, useContext, useState, useEffect } from "react";
import { path_url } from "../Config/config";
import {
  getToken,
  getUserInfo,
  saveUserInfo,
  clearToken,
  getAuthHeader,
  notifyLogout,
  decodeTokenPayload,
  isUUID
} from "../Utils/auth";

const UserContext = createContext(null);

// Sanitize stale localStorage values that may contain emails instead of UUIDs
const sanitizeLocalStorage = () => {
  const adminId = localStorage.getItem("adminId");
  if (adminId && !isUUID(adminId)) {
    console.warn("Stale non-UUID adminId detected, clearing:", adminId);
    localStorage.removeItem("adminId");
  }
  const userId = localStorage.getItem("userId");
  if (userId && !isUUID(userId)) {
    console.warn("Stale non-UUID userId detected, clearing:", userId);
    localStorage.removeItem("userId");
  }
  const userRaw = localStorage.getItem("entelipro_user");
  if (userRaw) {
    try {
      const parsed = JSON.parse(userRaw);
      if (parsed.id && !isUUID(parsed.id)) {
        console.warn("Stale non-UUID entelipro_user.id detected, clearing stored user.");
        localStorage.removeItem("entelipro_user");
      }
    } catch (_) {
      localStorage.removeItem("entelipro_user");
    }
  }
  // Also check for legacy "user" key
  const legacyUser = localStorage.getItem("user");
  if (legacyUser) {
    try {
      const parsed = JSON.parse(legacyUser);
      if (parsed.id && !isUUID(parsed.id)) {
        localStorage.removeItem("user");
      }
    } catch (_) {
      localStorage.removeItem("user");
    }
  }
  // Sanitize stale "name" key — if it looks like an email prefix (no spaces, has digits at end)
  // it was set by the old fallback logic and must be cleared so a real name can replace it.
  const storedName = localStorage.getItem("name");
  if (storedName) {
    // Detect email-prefix pattern: all lowercase with digits but no spaces (e.g. "muskanjain71689")
    const looksLikeEmailPrefix = /^[a-z0-9_.+-]+\d+$/i.test(storedName.trim()) || storedName.includes("@");
    if (looksLikeEmailPrefix) {
      console.warn("Stale email-prefix name detected, clearing:", storedName);
      localStorage.removeItem("name");
      // Also clear from entelipro_user if it has the same bad name
      const userRaw = localStorage.getItem("entelipro_user");
      if (userRaw) {
        try {
          const parsed = JSON.parse(userRaw);
          if (parsed.name === storedName) {
            parsed.name = null;
            localStorage.setItem("entelipro_user", JSON.stringify(parsed));
          }
        } catch (_) {}
      }
    }
  }
};

export const UserProvider = ({ children }) => {
  // Sanitize corrupted localStorage first before reading user info
  sanitizeLocalStorage();

  const [currentUser, setCurrentUser] = useState(() => getUserInfo());
  const [loadingUser, setLoadingUser] = useState(false);

  // Fetch user info from backend — uses GET /user which is the working endpoint
  const fetchAndStoreUser = async () => {
    const token = getToken();
    if (!token) {
      setCurrentUser(null);
      return;
    }

    setLoadingUser(true);

    // Immediate partial state from decoded JWT.
    // IMPORTANT: Do NOT fall back to email prefix as name — only use real name fields.
    const decoded = decodeTokenPayload();
    let jwtUser = null;
    if (decoded) {
      const rawId = [decoded.id, decoded.userId, decoded.usercid, decoded.user_id]
        .find(val => isUUID(val));

      const rawAdminId = [decoded.useradminid, decoded.adminId, decoded.admin_id, rawId]
        .find(val => isUUID(val));

      const emailStr = decoded.email || (typeof decoded.sub === "string" && decoded.sub.includes("@") ? decoded.sub : "");

      // Resolve name strictly — never use email prefix.
      // Priority: decoded.name > decoded.firstName+lastName > stored name from previous /user call
      const storedName = localStorage.getItem("name") || "";
      const jwtFullName = [
        decoded.name,
        (decoded.firstName && decoded.lastName) ? `${decoded.firstName} ${decoded.lastName}`.trim() : null,
        (decoded.first_name && decoded.last_name) ? `${decoded.first_name} ${decoded.last_name}`.trim() : null,
        decoded.given_name ? (decoded.family_name ? `${decoded.given_name} ${decoded.family_name}`.trim() : decoded.given_name) : null,
      ].find(n => n && typeof n === "string" && n.trim() && !n.includes("@"));

      // Use real name if available; keep storedName from previous /user fetch; last resort: null (not email prefix)
      const resolvedName = jwtFullName || (storedName && !storedName.includes("@") ? storedName : null);

      jwtUser = {
        id: rawId || null,
        useradminid: rawAdminId || rawId || null,
        name: resolvedName,   // null means no real name yet — will be filled by /user response
        email: emailStr,
        status: decoded.status || "Active",
        usertype: decoded.usertype || decoded.role || "USER"
      };
      // Only save and set if we have a real name or a valid id
      if (jwtUser.name || jwtUser.id) {
        if (jwtUser.name) localStorage.setItem("name", jwtUser.name);
        setCurrentUser(jwtUser);
      }
    }

    try {
      const baseUrl = path_url || "";

      // Primary: GET /user — this endpoint exists and returns { id, adminid, name, email, ... }
      const response = await fetch(`${baseUrl}/user`, {
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeader()
        }
      });

      if (response.ok) {
        const data = await response.json();
        // Backend returns: { id, adminid, name, email, usertype, ... }
        // Note: backend uses "adminid" (lowercase) not "useradminid"
        const rawId = isUUID(data.id) ? data.id : null;
        const rawAdminId = isUUID(data.adminid) ? data.adminid
          : isUUID(data.useradminid) ? data.useradminid
            : rawId;

        const userInfo = {
          id: rawId || null,
          useradminid: rawAdminId || rawId || null,
          // Resolve name: use real name fields only, never email prefix
          name: [
            data.name,
            data.userName,
            (data.firstName && data.lastName) ? `${data.firstName} ${data.lastName}`.trim() : null,
            (data.first_name && data.last_name) ? `${data.first_name} ${data.last_name}`.trim() : null,
          ].find(n => n && typeof n === "string" && n.trim() && !n.includes("@")) || jwtUser?.name || null,
          email: data.email || "",
          status: data.status || "Active",
          usertype: data.usertype || data.role || "USER"
        };

        console.log("=== USER INFO LOADED from /user ===");
        console.log("id (usercid):", userInfo.id);
        console.log("useradminid:", userInfo.useradminid);
        console.log("name:", userInfo.name);
        console.log("Is UUID format?", isUUID(userInfo.id));
        console.log("Raw backend response:", data);
        console.log("===================================");

        if (isUUID(userInfo.id)) {
          saveUserInfo(userInfo);
          setCurrentUser(userInfo);
        } else {
          // /user returned but without a valid UUID id — try /user/me as fallback
          console.warn("/user did not return a valid UUID id. Trying /user/me fallback...");
          const resp2 = await fetch(`${baseUrl}/user/me`, {
            headers: { "Content-Type": "application/json", ...getAuthHeader() }
          });
          if (resp2.ok) {
            const data2 = await resp2.json();
            const id2 = isUUID(data2.id) ? data2.id : null;
            const adminId2 = isUUID(data2.useradminid || data2.adminid)
              ? (data2.useradminid || data2.adminid)
              : id2;
            if (id2) {
              const userInfo2 = {
                id: id2,
                useradminid: adminId2,
                name: data2.name || userInfo.name,
                email: data2.email || userInfo.email,
                usertype: data2.usertype || "USER"
              };
              saveUserInfo(userInfo2);
              setCurrentUser(userInfo2);
            }
          }
        }

      } else if (response.status === 401) {
        clearToken();
        setCurrentUser(null);
        notifyLogout();
      } else {
        console.warn(`GET /user returned ${response.status} — keeping JWT-decoded user info for display.`);
        // jwtUser was already set above from JWT token; no further action needed
      }
    } catch (e) {
      console.warn("Failed to fetch user info from /user:", e);
    } finally {
      setLoadingUser(false);
    }
  };

  // Fetch user on app load if token exists
  useEffect(() => {
    if (getToken()) {
      fetchAndStoreUser();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Listen for login and logout events
  useEffect(() => {
    const onLogin = () => {
      fetchAndStoreUser();
    };

    const onLogout = () => {
      setCurrentUser(null);
    };

    window.addEventListener("entelipro:login", onLogin);
    window.addEventListener("entelipro:logout", onLogout);

    return () => {
      window.removeEventListener("entelipro:login", onLogin);
      window.removeEventListener("entelipro:logout", onLogout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = () => {
    clearToken();
    setCurrentUser(null);
    notifyLogout();
    window.location.href = "/login";
  };

  return (
    <UserContext.Provider
      value={{
        currentUser, // { id, useradminid, name, email, usertype }
        loadingUser,
        fetchAndStoreUser,
        logout,
        setCurrentUser // Added for compatibility with main branch
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

// Custom hook to access logged in user state anywhere
export const useCurrentUser = () => {
  const context = useContext(UserContext);

  // Fallback for when context is not available (outside provider)
  if (!context) {
    let parsed = null;
    try {
      const userStr = localStorage.getItem("entelipro_user") || localStorage.getItem("user");
      parsed = userStr ? JSON.parse(userStr) : null;
    } catch (e) {
      // Ignore parse errors
    }

    // Fallback to localStorage values with proper UUID validation
    const userId = localStorage.getItem("userId") || localStorage.getItem("usercid");
    const adminId = localStorage.getItem("adminId") || localStorage.getItem("useradminid");

    return {
      currentUser: parsed || {
        id: isUUID(userId) ? userId : null,
        useradminid: isUUID(adminId) ? adminId : null,
        name: localStorage.getItem("name") || "User",
        email: localStorage.getItem("email") || ""
      },
      loadingUser: false,
      fetchAndStoreUser: () => { },
      logout: () => {
        clearToken();
        window.location.href = "/login";
      }
    };
  }

  return context;
};

export default UserContext;